// Black & White — Photoshop-style Black & White adjustment filter.
//
// Converts the image to grayscale where the user independently controls how
// much each of six hue ranges (reds, yellows, greens, cyans, blues, magentas)
// contributes to the final monochrome luminance.  A separate Tint option
// blends the grayscale result with a user-chosen color.
//
// Algorithm:
//   1. Compute the hue (HSL) for each pixel.
//   2. Find the two adjacent color-range centers and interpolate between
//      their slider weights.
//   3. Multiply the Rec. 601 luma by the interpolated weight → gray value.
//   4. If tint is enabled, blend the gray toward the tint color.
//   5. Alpha is preserved unchanged.
//
// Selection masking is delegated to `applyFilterInPlace` (see Filter.ts).

import { PixelBuffer } from "../pixel/PixelBuffer";
import type { Selection } from "../selection/Selection";
import type { Filter } from "./Filter";

export interface BlackWhiteParams {
  /** Reds slider, -200..+300. Default 40. */
  reds: number;
  /** Yellows slider, -200..+300. Default 60. */
  yellows: number;
  /** Greens slider, -200..+300. Default 40. */
  greens: number;
  /** Cyans slider, -200..+300. Default 20. */
  cyans: number;
  /** Blues slider, -200..+300. Default 20. */
  blues: number;
  /** Magentas slider, -200..+300. Default 80. */
  magentas: number;
  /** When true, a color tint is applied on top of the grayscale result. */
  tint: boolean;
  /** Tint color as RGB (0..255 each). Ignored when tint is false. */
  tintColor: { r: number; g: number; b: number };
  /** Tint strength in percent, 0..100. 0 = no tint, 100 = full tint. */
  tintAmount: number;
}

const DEFAULT_PARAMS: BlackWhiteParams = {
  reds: 40,
  yellows: 60,
  greens: 40,
  cyans: 20,
  blues: 20,
  magentas: 80,
  tint: false,
  tintColor: { r: 255, g: 235, b: 190 },
  tintAmount: 0,
};

// ---------------------------------------------------------------------------
// Hue computation in degrees (0…360). Returns 0 for fully desaturated pixels.
// ---------------------------------------------------------------------------

function rgbToHue(r: number, g: number, b: number): number {
  const rn = r / 255;
  const gn = g / 255;
  const bn = b / 255;
  const max = Math.max(rn, gn, bn);
  const min = Math.min(rn, gn, bn);

  if (max === min) return 0;

  const d = max - min;
  let hue: number;
  if (max === rn) {
    hue = ((gn - bn) / d + (gn < bn ? 6 : 0)) * 60;
  } else if (max === gn) {
    hue = ((bn - rn) / d + 2) * 60;
  } else {
    hue = ((rn - gn) / d + 4) * 60;
  }

  return ((hue % 360) + 360) % 360;
}

// ---------------------------------------------------------------------------
// Hue-range centers (degrees): reds=0, yellows=60, greens=120, cyans=180,
// blues=240, magentas=300.  Each spans 60°; a pixel's hue falls between two
// adjacent centers and the slider weights are linearly interpolated.
// ---------------------------------------------------------------------------

/** Convert a slider value in [-200, +300] to a weight where 0 → 1.0. */
function sliderWeight(v: number): number {
  return Math.max(0, (v + 100) / 100);
}

/**
 * Interpolate between two adjacent colour-range slider weights based on hue.
 * Each 60° segment maps to a pair of consecutive sliders:
 *   0-60: reds(0) → yellows(1),  60-120: yellows(1) → greens(2),
 *   120-180: greens(2) → cyans(3),  180-240: cyans(3) → blues(4),
 *   240-300: blues(4) → magentas(5),  300-360: magentas(5) → reds(0).
 */
function interpolateWeight(hue: number, w: number[]): number {
  const segmentAngle = ((hue % 360) + 360) % 360 / 60; // 0..6
  const segIdx = Math.floor(segmentAngle) % 6;
  const t = segmentAngle - Math.floor(segmentAngle);
  const nextIdx = (segIdx + 1) % 6;
  return w[segIdx]! * (1 - t) + w[nextIdx]! * t;
}

// ---------------------------------------------------------------------------
// Filter export
// ---------------------------------------------------------------------------

export const BlackWhite: Filter<BlackWhiteParams> = {
  id: "black-white",
  displayName: "Black & White",
  defaultParams: DEFAULT_PARAMS,

  apply(
    input: PixelBuffer,
    params: BlackWhiteParams,
    _selection: Selection | null,
  ): PixelBuffer {
    const w = input.width;
    const h = input.height;
    const out = new PixelBuffer(w, h, input.mode);
    const src = input.data;
    const dst = out.data;
    const n = w * h;

    // Pre-compute per-slider weights from the user's parameters.
    const weights = [
      sliderWeight(clampRange(params.reds, -200, 300)),
      sliderWeight(clampRange(params.yellows, -200, 300)),
      sliderWeight(clampRange(params.greens, -200, 300)),
      sliderWeight(clampRange(params.cyans, -200, 300)),
      sliderWeight(clampRange(params.blues, -200, 300)),
      sliderWeight(clampRange(params.magentas, -200, 300)),
    ];

    for (let i = 0; i < n; i++) {
      const o = i * 4;
      const r = src[o] ?? 0;
      const g = src[o + 1] ?? 0;
      const b = src[o + 2] ?? 0;
      const a = src[o + 3] ?? 0;

      if (a === 0) {
        dst[o] = 0;
        dst[o + 1] = 0;
        dst[o + 2] = 0;
        dst[o + 3] = 0;
        continue;
      }

      let gray: number;

      if (r === g && g === b) {
        // Already fully desaturated — no color mapping to apply.
        gray = r;
      } else {
        const hue = rgbToHue(r, g, b);
        const weight = interpolateWeight(hue, weights);
        // Rec. 601 luma coefficients.
        const luma = 0.299 * r + 0.587 * g + 0.114 * b;
        gray = clampRange(luma * weight, 0, 255);
      }

      if (params.tint && params.tintAmount > 0) {
        const t = clampRange(params.tintAmount, 0, 100) / 100;
        const inv = 1 - t;
        const tc = params.tintColor;
        dst[o] = clampRange(gray * inv + tc.r * t, 0, 255);
        dst[o + 1] = clampRange(gray * inv + tc.g * t, 0, 255);
        dst[o + 2] = clampRange(gray * inv + tc.b * t, 0, 255);
      } else {
        dst[o] = clampRange(gray, 0, 255);
        dst[o + 1] = clampRange(gray, 0, 255);
        dst[o + 2] = clampRange(gray, 0, 255);
      }

      dst[o + 3] = a;
    }

    return out;
  },
};

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function clampRange(v: number, lo: number, hi: number): number {
  if (!isFinite(v)) return 0;
  if (v < lo) return lo;
  if (v > hi) return hi;
  return v;
}
